import { Link } from "react-router-dom";
import Seo from "../components/Seo.jsx";

// Africa destination page. Frontend-only; mirrors the service-page structure
// but leans more cinematic/emotional since Africa is central to the brand.
// Reuses existing destination WebP images for a light, performant image strip.

const FEATURE_IMAGES = [
  { img: "/destinations/destination-kenya.webp", region: "East Africa", tag: "Safaris, lakes & highlands" },
  { img: "/destinations/destination-morocco.webp", region: "North Africa", tag: "Medinas & desert nights" },
  { img: "/destinations/destination-namibia.webp", region: "Southern Africa", tag: "Dunes, coast & wildlife" },
];

const REGIONS = [
  { t: "West Africa", d: "Lagos, Accra, Abidjan and beyond — vibrant cities, culture, music and coastlines." },
  { t: "East Africa", d: "Kenya, Tanzania, Rwanda and Uganda — safaris, mountains, lakes and slow luxury." },
  { t: "Southern Africa", d: "Cape Town, Johannesburg, Namibia and Botswana — coast, desert, wine, wildlife and design." },
  { t: "North Africa", d: "Morocco and Egypt — medinas, desert stays, ancient cities and cinematic landscapes." },
  { t: "Island escapes", d: "Zanzibar, Mauritius, Seychelles and Cape Verde — beach days, resorts and soft arrivals." },
  { t: "Multi-country journeys", d: "Africa trips shaped across borders, seasons and travel styles." },
];

const STYLES = [
  "City break", "Safari", "Beach", "Culture", "Food", "Music", "Fashion", "Desert",
  "Mountains", "Wellness", "Family", "Honeymoon", "Luxury", "Diaspora", "Business",
];

const STEPS = [
  { n: "01", t: "Tell us your destination or dream", d: "A country, a city, or just a feeling you're chasing." },
  { n: "02", t: "We shape the route and timing", d: "Built around seasons, connections and your pace." },
  { n: "03", t: "You confirm flights, stays & experiences", d: "Clear options and final pricing before you pay." },
  { n: "04", t: "We support you before and during travel", d: "A real team on your side, start to finish." },
];

export default function Africa() {
  return (
    <div className="page">
      <Seo
        title="Africa Travel, Curated — 513Travel"
        description="Explore Africa through Lagos weekends, Accra escapes, Serengeti safaris, Cape Town coastlines, Marrakech medinas, Kigali hills and more."
        path="/africa"
      />
      {/* ===== Hero ===== */}
      <section className="page-hero">
        <div className="container">
          <div className="eyebrow">Africa</div>
          <h1>The continent, curated</h1>
          <p className="lede">
            From Lagos weekends and Accra escapes to Serengeti safaris, Cape Town
            coastlines, Marrakech medinas and Kigali hills, 513Travel helps you
            explore Africa with care, comfort and local understanding.
          </p>
          <div className="page-actions">
            <Link to="/plan-a-trip" className="btn gold">Plan an African escape</Link>
            <Link to="/contact" className="btn outline">Talk to us</Link>
          </div>
        </div>
      </section>

      {/* ===== Cinematic image strip ===== */}
      <section className="section" style={{ paddingTop: 0 }}>
        <div className="container">
          <div className="grid grid-3">
            {FEATURE_IMAGES.map((f) => (
              <Link to="/plan-a-trip" className="dest-card" key={f.region}>
                <div className="dest-image">
                  <img src={f.img} alt={`${f.region} — ${f.tag}`} loading="lazy" decoding="async" />
                </div>
                <div className="dest-body">
                  <h3>{f.region}</h3>
                  <p>{f.tag}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Where Africa begins ===== */}
      <section className="section section--warm">
        <div className="container">
          <div className="section-head">
            <div className="eyebrow">Where Africa begins</div>
            <h2>One continent, many journeys</h2>
            <p>Africa isn't one trip — it's dozens. Here's where most of our travellers start.</p>
          </div>
          <div className="grid grid-3">
            {REGIONS.map((r) => (
              <div className="service-card" key={r.t}>
                <h3>{r.t}</h3>
                <p>{r.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Africa styles ===== */}
      <section className="section">
        <div className="container">
          <div className="section-head">
            <div className="eyebrow">More than safari</div>
            <h2>Africa styles we understand</h2>
            <p>Modern, diverse and premium — from city weekends and fashion to deserts, music and slow luxury.</p>
          </div>
          <div className="route-chips">
            {STYLES.map((s) => (
              <span className="route-chip" key={s}>{s}</span>
            ))}
          </div>
        </div>
      </section>

      {/* ===== How it works ===== */}
      <section className="section section--warm">
        <div className="container">
          <div className="section-head">
            <div className="eyebrow">How it works</div>
            <h2>How African trips work</h2>
          </div>
          <div className="grid grid-4">
            {STEPS.map((s) => (
              <div className="step-card" key={s.n}>
                <span className="step-num" aria-hidden="true">{s.n}</span>
                <h3>{s.t}</h3>
                <p>{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Trust note ===== */}
      <section className="section" style={{ paddingTop: 0 }}>
        <div className="container">
          <p className="demo-note demo-note--center">
            Africa travel works best with local knowledge. Routes, seasons,
            availability and requirements can change, so we confirm the important
            details before you pay.
          </p>
        </div>
      </section>

      {/* ===== Final CTA ===== */}
      <section className="section section--green">
        <div className="container">
          <div className="feature-copy feature-solo">
            <div className="eyebrow">Ready for Africa</div>
            <h2>Where in Africa should we take you?</h2>
            <p>Tell us the country, city or feeling you have in mind. We'll help shape the trip around your dates, budget and travel style.</p>
            <div className="page-actions">
              <Link to="/plan-a-trip" className="btn light">Plan an African escape</Link>
              <Link to="/track-trip" className="btn outline-light">Track existing trip</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
